// src/components/monthly/ApplyForms/WorkInfoForm.tsx
import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { ApplyFormData } from '../ApplyFormComponent1';
import { PhoneCodeSelect } from './PhoneCode/PhoneCodeSelect';
import { useLocation } from 'react-router-dom';

export const WorkInfoForm: React.FC = () => {
  const {
    register,
    formState: { errors },
    control,
    watch,
  } = useFormContext<ApplyFormData>();
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  const inputStyles =
    'w-full border border-black mt-1 h-[27px] text-sm px-1 py-0';
  const labelStyles = 'block text-gray-800';

  /** 3桁ごとにカンマを入れる */
  const formatNumber = (value?: string | number): string => {
    if (value == null || value === '') return '';
    const num = typeof value === 'string' ? Number(value) : value;
    return isNaN(num) ? '' : num.toLocaleString();
  };

  // リアルタイムに入力値を取得
  const annualIncome = watch('workData.annualIncome');

  return (
    <div className="w-full max-md:max-w-md max-md:mx-auto px-5 bg-[#f6f3e6] mt-5">
      {/* 勤務先名称 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Company Name' : '勤務先名称'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-1">
          <input
            type="text"
            placeholder={
              useEnglish ? 'e.g. XYZ Corporation' : '例）〇〇株式会社'
            }
            className={`${inputStyles} ${
              errors.workData?.companyName ? 'border-red-500' : ''
            }`}
            {...register('workData.companyName', {
              required: useEnglish
                ? 'Company Name is required'
                : '勤務先名称が未入力です',
            })}
          />
          {errors.workData?.companyName && (
            <>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3 pb-4">
                !
              </span>
              <div className="text-red-500 text-sm mt-1">
                {errors.workData.companyName.message?.toString()}
              </div>
            </>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {useEnglish
            ? 'If self-employed, please enter "Self-employed".'
            : '※個人事業主の方は「個人事業主」とご入力ください'}
        </p>
      </div>

      {/* 勤務先所属 (部署) */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Department' : '勤務先所属'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-1">
          <input
            type="text"
            placeholder={
              useEnglish ? 'e.g. Sales Department' : '例）〇〇部〇〇課'
            }
            className={`${inputStyles} ${
              errors.workData?.department ? 'border-red-500' : ''
            }`}
            {...register('workData.department', {
              required: useEnglish
                ? 'Department is required'
                : '所属部署(勤務先所属)が未入力です',
            })}
          />
          {errors.workData?.department && (
            <>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3 pb-4">
                !
              </span>
              <div className="text-red-500 text-sm mt-1">
                {errors.workData.department.message?.toString()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 勤務先住所 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Company Address' : '勤務先住所'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-1">
          <input
            type="text"
            placeholder={
              useEnglish ? 'e.g. 123 Main St, Tokyo' : '例）東京都渋谷区〇〇'
            }
            className={`${inputStyles} ${
              errors.workData?.companyAddress ? 'border-red-500' : ''
            }`}
            {...register('workData.companyAddress', {
              required: useEnglish
                ? 'Company Address is required'
                : '勤務先住所が未入力です',
            })}
          />
          {errors.workData?.companyAddress && (
            <>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3 pb-4">
                !
              </span>
              <div className="text-red-500 text-sm mt-1">
                {errors.workData.companyAddress.message?.toString()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 勤務先電話番号 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Company Phone' : '勤務先電話番号'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-1">
          <Controller
            name="workData.companyPhone"
            control={control}
            rules={{
              required: useEnglish
                ? 'Company Phone is required'
                : '勤務先電話番号が未入力です',
            }}
            render={({ field }) => <PhoneCodeSelect {...field} />}
          />
          {errors.workData?.companyPhone && (
            <>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                !
              </span>
              <div className="text-red-500 text-sm mt-1">
                {errors.workData.companyPhone.message?.toString()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 業種 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Industry' : '業種'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-1">
          <input
            type="text"
            placeholder={useEnglish ? 'e.g. Real Estate' : '例）不動産業'}
            className={`${inputStyles} ${
              errors.workData?.industry ? 'border-red-500' : ''
            }`}
            {...register('workData.industry', {
              required: useEnglish
                ? 'Industry is required'
                : '業種が未入力です',
            })}
          />
          {errors.workData?.industry && (
            <>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                !
              </span>
              <div className="text-red-500 text-sm mt-1">
                {errors.workData.industry.message?.toString()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 職種 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Job Type' : '職種'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-1">
          <input
            type="text"
            placeholder={useEnglish ? 'e.g. Developer' : '例）開発職'}
            className={`${inputStyles} ${
              errors.workData?.jobType ? 'border-red-500' : ''
            }`}
            {...register('workData.jobType', {
              required: useEnglish
                ? 'Job Type is required'
                : '職種が未入力です',
            })}
          />
          {errors.workData?.jobType && (
            <>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                !
              </span>
              <div className="text-red-500 text-sm mt-1">
                {errors.workData.jobType.message?.toString()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 役職 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Position' : '役職'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-1">
          <input
            type="text"
            placeholder={useEnglish ? 'e.g. Manager' : '例）課長'}
            className={`${inputStyles} ${
              errors.workData?.position ? 'border-red-500' : ''
            }`}
            {...register('workData.position', {
              required: useEnglish
                ? 'Position is required'
                : '役職が未入力です',
            })}
          />
          {errors.workData?.position && (
            <>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                !
              </span>
              <div className="text-red-500 text-sm mt-1">
                {errors.workData.position.message?.toString()}
              </div>
            </>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {useEnglish
            ? 'If you have no position, please enter "None".'
            : '※役職のない方は「なし」とご入力ください'}
        </p>
      </div>

      {/* 年収 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Annual Income' : '年収'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="mt-1">
          <div className="flex items-center">
            <Controller
              name="workData.annualIncome"
              control={control}
              rules={{
                required: useEnglish
                  ? 'Annual Income is required'
                  : '年収が未入力です',
                pattern: {
                  value: /^\d+$/,
                  message: useEnglish
                    ? 'Annual Income must be a number (unit: yen)'
                    : '年収は数値を入力してください (単位: 円)',
                },
              }}
              render={({ field }) => (
                <input
                  type="text"
                  placeholder={useEnglish ? 'e.g. 5,000,000' : '例）5,000,000'}
                  className={`${inputStyles} ${
                    errors.workData?.annualIncome ? 'border-red-500' : ''
                  }`}
                  value={formatNumber(field.value)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/,/g, '');
                    field.onChange(raw);
                  }}
                />
              )}
            />
            <span className="ml-2 text-sm">{useEnglish ? 'yen' : '円'}</span>
          </div>
          {errors.workData?.annualIncome && (
            <div className="text-red-500 text-sm mt-1">
              {errors.workData.annualIncome.message?.toString()}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          {useEnglish
            ? 'If you receive income from abroad, please convert it to Japanese Yen.'
            : '※外国で受給の方は、日本円に換算した額をご記入ください'}
        </p>
      </div>
    </div>
  );
};
